import { useContext } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { API } from '../lib/endpoints'
import { AuthContext } from '../auth/AuthProvider'
import type { Feature, FeatureFlag, FeatureFlagWritePayload, TenantMinimal } from '../types/api'

export const featureFlagKeys = {
  all: ['feature-flags'] as const,
  list: () => [...featureFlagKeys.all, 'list'] as const,
}

export const featureKeys = {
  all: ['features'] as const,
  list: () => [...featureKeys.all, 'list'] as const,
}

export const tenantKeys = {
  all: ['tenants'] as const,
  list: () => [...tenantKeys.all, 'list'] as const,
}

export function useFeatureFlags() {
  return useQuery({
    queryKey: featureFlagKeys.list(),
    queryFn: () => api.get<FeatureFlag[]>(API.featureFlags.list),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useFeatures() {
  return useQuery({
    queryKey: featureKeys.list(),
    queryFn: () => api.get<Feature[]>(API.features.list),
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Check if a specific feature flag is enabled.
 *
 * Checks the new Feature model first (with per-tenant overrides),
 * then falls back to the legacy FeatureFlag model.
 *
 * Loading semantics: returns `false` while both feature sources are still
 * fetching. The previous default was `true`, which caused gated effects
 * (e.g. `enabled && useSomeHook(...)`) to briefly fire on initial render
 * and trigger 403s that lingered in the console even after the flag
 * settled to `false`. A conservative `false` during loading is the
 * right default for permission-style gating.
 *
 * Once feature/flag data is loaded but the key is genuinely unknown,
 * we still fall through to `true` so callers using ad-hoc keys without
 * a backend record keep the historical behavior.
 */
export function useFeatureFlag(key: string): boolean {
  const { data: features } = useFeatures()
  const { data: flags } = useFeatureFlags()
  const auth = useContext(AuthContext)
  const user = auth?.user

  // While both sources are still loading, default off — see docstring.
  if (features === undefined && flags === undefined) return false

  // Check new Feature model first (authoritative source)
  if (features) {
    const feature = features.find((f) => f.key === key)
    if (feature) {
      // Check per-tenant override
      if (user?.tenant && feature.overrides) {
        const override = feature.overrides.find((o) => o.tenant === user.tenant)
        if (override) return override.enabled
      }
      return feature.default_enabled
    }
  }

  // Fallback to legacy FeatureFlag model
  if (!flags) return true
  const flag = flags.find((f) => f.key === key)
  if (!flag) return true
  return flag.enabled_global
}

export function useTenants() {
  return useQuery({
    queryKey: tenantKeys.list(),
    queryFn: () => api.get<TenantMinimal[]>(API.tenants.list),
    staleTime: 10 * 60 * 1000, // 10 minutes
  })
}

export function useCreateFeatureFlag() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: FeatureFlagWritePayload) =>
      api.post<FeatureFlag>(API.featureFlags.list, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: featureFlagKeys.all })
    },
  })
}

export function useUpdateFeatureFlag() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...payload }: Partial<FeatureFlagWritePayload> & { id: number }) =>
      api.patch<FeatureFlag>(API.featureFlags.detail(id), payload),
    onMutate: async (variables) => {
      await qc.cancelQueries({ queryKey: featureFlagKeys.list() })
      const prev = qc.getQueryData<FeatureFlag[]>(featureFlagKeys.list())
      if (prev) {
        qc.setQueryData<FeatureFlag[]>(featureFlagKeys.list(), prev.map((f) =>
          f.id === variables.id ? { ...f, ...variables } : f
        ))
      }
      return { prev }
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) {
        qc.setQueryData(featureFlagKeys.list(), context.prev)
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: featureFlagKeys.all })
    },
  })
}

export function useDeleteFeatureFlag() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete(API.featureFlags.detail(id)),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: featureFlagKeys.list() })
      const prev = qc.getQueryData<FeatureFlag[]>(featureFlagKeys.list())
      if (prev) {
        qc.setQueryData<FeatureFlag[]>(featureFlagKeys.list(), prev.filter((f) => f.id !== id))
      }
      return { prev }
    },
    onError: (_err, _id, context) => {
      if (context?.prev) {
        qc.setQueryData(featureFlagKeys.list(), context.prev)
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: featureFlagKeys.all })
    },
  })
}

export function useToggleFeatureFlag() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, enabled_global }: { id: number; enabled_global: boolean }) =>
      api.patch<FeatureFlag>(API.featureFlags.detail(id), { enabled_global }),
    onMutate: async ({ id, enabled_global }) => {
      await qc.cancelQueries({ queryKey: featureFlagKeys.list() })
      const prev = qc.getQueryData<FeatureFlag[]>(featureFlagKeys.list())
      if (prev) {
        qc.setQueryData<FeatureFlag[]>(featureFlagKeys.list(), prev.map((f) =>
          f.id === id ? { ...f, enabled_global } : f
        ))
      }
      return { prev }
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) {
        qc.setQueryData(featureFlagKeys.list(), context.prev)
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: featureFlagKeys.all })
    },
  })
}
